/* eslint-disable indent */
import {
	Box,
	Button,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
} from '@mui/material'
import React from 'react'
import { WorkItem } from '../../../types/Work/WorkItem'
import { StyledTableCell } from '../../core/StyledTableCell'
import { lazy } from 'react'

import { SurveyWorkItemRow } from './SurveyWorkItemRow'
import { ComponentChangeOrder } from '../../../types/Work/Survey'
import { createChangeOrder } from '../../../services/quantitySurveyService'
import { deleteAttachment, getAttachment, listAttachments, uploadAttachments } from '../../../services/attachmentService'

type SurveyBacklogListProps = {
	hierarchy: WorkItem | undefined
	selectedSurveyId: number | null
	refreshData: () => Promise<void>
	surveyCompleted: boolean
}

const ViewAttachmentsPopup = lazy(() =>
	import('../../attachments/ViewAttachmentsPopup').then((module) => ({
		default: module.ViewAttachmentsPopup,
	})),
)

export function SurveyBacklogList({ hierarchy, selectedSurveyId, refreshData, surveyCompleted }: SurveyBacklogListProps) {
	const [isViewingAttachment, setIsViewingAttachment] = React.useState<boolean>(false)
	const [attachmentWorkitemId, setAttachmentWorkitemId] = React.useState<number>()
	
	const selectAttachments = (workItemId: number) => {
		setAttachmentWorkitemId(workItemId)
		setIsViewingAttachment(true)
	}

	const handleComponentValueChange = async (
		component: ComponentChangeOrder,
		newValue: number | null,
		blobs?: Blob[],
	) => {
		await createChangeOrder({
			componentId: component.componentId,
			changeOrderComponentId: component.changeOrderComponentId,
			originalValue: component.originalValue,
			proposedValue: newValue,
			newAttachmentCount: blobs?.length ?? 0,
			surveyId: selectedSurveyId ?? 0,
		}, blobs)
		await refreshData()
	}

	return (
		<>
			<TableContainer>
				<Table stickyHeader>
					<TableHead>
						<TableRow>
							<StyledTableCell />
							<StyledTableCell align='center'>Type</StyledTableCell>
							<StyledTableCell align='center'>Constraints</StyledTableCell>
							<StyledTableCell align='center'>Craft</StyledTableCell>
							<StyledTableCell>Name</StyledTableCell>
							<StyledTableCell align='center'>Info</StyledTableCell>
							<StyledTableCell align='center'>Release Date</StyledTableCell>
							<StyledTableCell align='center'>Attachments</StyledTableCell>
							<StyledTableCell align='center'>Action</StyledTableCell>
						</TableRow>
					</TableHead>

					<TableBody>
						{hierarchy && (
							<SurveyWorkItemRow
								workItem={hierarchy}
								key={hierarchy.id}
								selectAttachments={selectAttachments}
								layer={1}
								refreshData={refreshData}
								handleComponentValueChange={handleComponentValueChange}
								surveyCompleted={surveyCompleted}
							/>
						)}
					</TableBody>
				</Table>
			</TableContainer>
			{attachmentWorkitemId && (
				<ViewAttachmentsPopup
					handleClose={() => {
						setIsViewingAttachment(false)
						setAttachmentWorkitemId(undefined)
					}}
					open={isViewingAttachment}
					itemId={attachmentWorkitemId}
					attachmentOperations={{
						listAttachments: (id) => listAttachments(id, 'workitem', null),
						getAttachment: (id) => getAttachment(id, 'workitem', null),
						uploadAttachment: (files, id, overwrite, newAnnotations, setUploadProgress) =>
							uploadAttachments(files, id, overwrite, 'workitem', newAnnotations, setUploadProgress),
						deleteAttachment: (id) => deleteAttachment(id, 'workitem'),
					}}
				/>
			)}
		</>
	)
}
